import { Chip } from '@mui/material'
import { gridStringOrNumberComparator } from '@mui/x-data-grid-premium'
import type { ObjectPropertyDefinition } from 'types/graphql'
import { isUUID } from 'validator'

import DayTableHeader from 'src/components/DataTables/DayTableHeader'
import PropertyDisplay from 'src/components/Properties/PropertyDisplay'
import propertyDisplayOverrides from 'src/components/Properties/propertyDisplayOverrides'
import { logger } from 'src/lib/logger'
import { PropertyTypes, type NativeObjectType } from 'src/lib/objects'
import {
  StandardProperties,
  StandardPropertyEditPolicy,
  StandardPropertyTablePolicy,
} from 'src/lib/Properties/properties'

import { hasOptions } from '../../Properties/formattingAndValidation'
import RenderPropertyEditCell from '../../Properties/RenderPropertyEditCell'
import Row from '../../Row/Row'

const cellRendererRowSx = {
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  justifyContent: 'center',
}

const getCellRenderer = ({
  propertyDefinition,
  workspaceId,
  isCustomProperty,
}) => {
  if (isCustomProperty) {
    return (params) => {
      const { row } = params
      const object = row.object

      if (
        !row.object &&
        params.rowNode?.groupingKey &&
        ![undefined, null, ''].includes(params?.value) &&
        params.rowNode?.groupingField === propertyDefinition?.id
      ) {
        return (
          <Chip
            label={params?.value}
            size="small"
          />
        )
      } else if (
        params.rowNode?.groupingField &&
        !params.rowNode?.groupingKey
      ) {
        return null
      }

      return (
        object && (
          <Row
            sx={{
              height: '100%',
              width: '100%',

              '& .picklist-options-row': {
                flexWrap: 'nowrap',
              },
            }}
          >
            <PropertyDisplay
              propertyDefinition={propertyDefinition}
              value={object?.properties?.custom?.[propertyDefinition?.id]}
              workspaceId={workspaceId}
              objectId={object.objectId}
              objectType={object.objectType}
              justify="flex-start"
            />
          </Row>
        )
      )
    }
  } else {
    return (params) => {
      const { row } = params
      const value = row?.object?.properties?.standard?.[propertyDefinition?.id]

      if (
        !row.object &&
        params.rowNode?.groupingKey &&
        params.rowNode?.groupingField === propertyDefinition?.id
      ) {
        const overrideRenderer =
          propertyDisplayOverrides[propertyDefinition.objectTypeId]?.[
            propertyDefinition?.id
          ]?.propertyRenderer

        const object = {
          workspaceId,
          properties: {
            standard: {
              [propertyDefinition.id]: { value: params?.value },
            },
          },
        }

        if (![undefined, null, ''].includes(params?.value)) {
          if (overrideRenderer) {
            return overrideRenderer({
              value: params?.value,
              object,
              workspaceId,
            })
          } else {
            return (
              <Chip
                label={params?.value?.toString()}
                size="small"
              />
            )
          }
        } else {
          logger.dev('RENDERING GROUP HEADER', {
            params,
            propertyDefinition,
          })
          return null
        }
      } else if (
        params.rowNode?.groupingField &&
        !params.rowNode?.groupingKey
      ) {
        return null
      }

      return (
        <Row sx={cellRendererRowSx}>
          <PropertyDisplay
            propertyDefinition={propertyDefinition}
            value={value}
            object={row.object}
            objectId={row.id}
            objectType={row.objectType}
            workspaceId={workspaceId}
            justify="flex-start"
          />
        </Row>
      )
    }
  }
}

export const buildColumns = (
  workspaceId: string,
  objectType: NativeObjectType,
  propertyDefinitions: Record<NativeObjectType, ObjectPropertyDefinition>,
  columnOverrides?: {
    propertyDefinitionId: string
    columnParameter: string
    parameterValue: any
  }[]
) => {
  if (!StandardProperties[objectType]) {
    logger.warn(`Standard properties not found for objectType: ${objectType}`)
    return []
  }

  const propertyColumns = Object.values(propertyDefinitions)
    .map((propDef) => {
      const isCustomProperty = isUUID(propDef.id)
      const standardProperty = StandardProperties[objectType]?.[propDef.id]

      if (
        standardProperty &&
        standardProperty.tablePolicy === StandardPropertyTablePolicy.HIDE
      ) {
        return null
      }

      const editable =
        isCustomProperty ||
        standardProperty?.editPolicy === StandardPropertyEditPolicy.ALLOW

      const baseColumn = {
        field: propDef.id,
        headerName: propDef.name,
        description: propDef.description,
        editable,
        aggregable: false,
        objectType,
        renderHeader: DayTableHeader,
        headerAlign: 'left',
      }

      const parameterOverrides = Array.isArray(columnOverrides)
        ? (columnOverrides || [])
            ?.filter((override) => override.propertyDefinitionId === propDef.id)
            .reduce((acc, override) => {
              return {
                ...acc,
                [override.columnParameter]: override.parameterValue,
              }
            }, {})
        : {}

      const columnTypeDef = ColumnDefinitions[propDef.propertyTypeId]

      const widthOverride = !isCustomProperty
        ? propertyDisplayOverrides[objectType]?.[propDef.id]?.tableWidth
        : undefined

      if (widthOverride) {
        columnTypeDef.flex = 0
        columnTypeDef.width = widthOverride
      }

      const combinedColumn = {
        ...baseColumn,
        ...columnTypeDef,
        renderCell: getCellRenderer({
          propertyDefinition: propDef,
          workspaceId,
          isCustomProperty,
        }),

        renderEditCell: (params) => (
          <RenderPropertyEditCell
            propertyDefinition={propDef}
            objectType={objectType}
            workspaceId={workspaceId}
            params={params}
            justify="flex-start"
          />
        ),
      }

      let finalColumn
      // For picklist/multipicklist, add options
      if (hasOptions(propDef.propertyTypeId)) {
        finalColumn = {
          ...combinedColumn,
          valueOptions: Array.isArray(propDef.options) ? propDef.options : [], // Add options directly to column definition
          getOptionValue: (option) => option.name, // Use the actual text so it's searchable and filterable as a string
          getOptionLabel: (option) => option.name, // Tell MUI how to get the label from our option objects
        }
      } else {
        finalColumn = combinedColumn
      }

      return {
        ...finalColumn,
        ...parameterOverrides,
      }
    })
    .filter(Boolean)

  const updatedAtColumn = {
    field: 'updatedAt',
    headerName: 'Updated',
    flex: 0,
    width: 100,
    type: 'dateTime',
    renderCell: ({ value }) => {
      return (
        value && (
          <Row sx={cellRendererRowSx}>
            <PropertyDisplay
              propertyDefinition={{
                id: 'updatedAt',
                name: 'Updated',
                propertyTypeId: PropertyTypes.DateTime,
                propertyType: {
                  id: PropertyTypes.DateTime,
                  name: 'DateTime',
                  description: '',
                },
                aiManaged: false,
                createdAt: value.value,
                updatedAt: value.value,
                objectTypeId: value.objectType,
                options: null,
                useWeb: false,
                workspaceId,
              }}
              objectId={value.id}
              objectType={value.objectType}
              workspaceId={workspaceId}
              value={{ value }}
              justify="flex-start"
            />
          </Row>
        )
      )
    },
    valueGetter: ColumnDefinitions[PropertyTypes.DateTime].valueGetter,
  }

  const _createdAtColumn = {
    field: 'createdAt',
    headerName: 'Created',
    flex: 0,
    width: 100,
    visible: false,
    type: 'dateTime',
    renderCell: ({ value }) => {
      return (
        value && (
          <Row sx={cellRendererRowSx}>
            <PropertyDisplay
              propertyDefinition={{
                id: 'createdAt',
                name: 'Created',
                propertyTypeId: PropertyTypes.DateTime,
                propertyType: {
                  id: PropertyTypes.DateTime,
                  name: 'DateTime',
                  description: '',
                },
                aiManaged: false,
                createdAt: value.value,
                updatedAt: value.value,
                objectTypeId: value.objectType,
                options: null,
                useWeb: false,
                workspaceId,
              }}
              objectId={value.id}
              objectType={value.objectType}
              workspaceId={workspaceId}
              value={{ value }}
              justify="flex-start"
            />
          </Row>
        )
      )
    },
    valueGetter: ColumnDefinitions[PropertyTypes.DateTime].valueGetter,
  }

  const allColumns = [...propertyColumns, updatedAtColumn] //, createdAtColumn]

  return allColumns
}

export const getColumnVisibilityModelInitial = (
  objectType: NativeObjectType
) => {
  const nonDefaultColumns = Object.entries(
    StandardProperties?.[objectType] || {}
  )
    .filter(
      ([_, property]) =>
        property.tablePolicy === StandardPropertyTablePolicy.SHOW
    )
    .map(([key]) => key)

  const columnVis = nonDefaultColumns.reduce((acc, column) => {
    acc[column] = false
    return acc
  }, {})

  return columnVis
}

const ColumnWidths = {
  STANDARD: 200,
  TINY: 96,
  SMALL: 150,
  MEDIUM: 200,
  LARGE: 200,
  XLARGE: 300,
}

const sortComparatorGenericWithoutNulls = (sortDirection) => {
  const modifier = sortDirection === 'desc' ? -1 : 1
  return (value1, value2, cellParams1, cellParams2) => {
    if (value1 === null) {
      return 1
    }
    if (value2 === null) {
      return -1
    }
    return (
      modifier *
      gridStringOrNumberComparator(value1, value2, cellParams1, cellParams2)
    )
  }
}

const DatetimeColumnDefinition = {
  type: 'dateTime',
  width: ColumnWidths.TINY,
  groupable: false,
  filterable: true,
  valueGetter: (value) => {
    if (!value) {
      return null
    }
    if (typeof value === 'object') {
      const isDate = Object.prototype.toString.call(value) === '[object Date]'
      if (isDate) {
        return value
      } else {
        try {
          return new Date(value)
        } catch (error) {
          return null
        }
      }
    }
    if (typeof value === 'string') {
      try {
        return new Date(value)
      } catch (error) {
        return null
      }
    }
    return null
  },
}

export const ColumnDefinitions = {
  [PropertyTypes.TextArea]: {
    type: 'string',
    flex: 1,
    groupable: true,
    filterable: true,
    minWidth: ColumnWidths.STANDARD,
  },
  [PropertyTypes.Integer]: {
    type: 'number',
    width: ColumnWidths.STANDARD,
    groupable: true,
    filterable: true,
    valueGetter: (value) => {
      if ([undefined, null].includes(value)) {
        return null
      }
      if (typeof value === 'number') {
        return value
      }
      try {
        return Number(value)
      } catch (error) {
        return null
      }
    },
    getSortComparator: sortComparatorGenericWithoutNulls,
  },
  [PropertyTypes.Calculated]: {
    type: 'string',
    width: ColumnWidths.STANDARD,
    groupable: true,
    filterable: true,
    getSortComparator: sortComparatorGenericWithoutNulls,
  },
  [PropertyTypes.Float]: {
    type: 'number',
    width: ColumnWidths.STANDARD,
    groupable: true,
    filterable: true,
    valueGetter: (value) => {
      if (!value) {
        return null
      }
      if (typeof value === 'number') {
        return value
      }
      try {
        return Number(value)
      } catch (error) {
        return null
      }
    },
  },
  [PropertyTypes.Boolean]: {
    type: 'boolean',
    width: ColumnWidths.TINY,
    groupable: true,
    filterable: true,
    valueGetter: (value) => {
      if (['', null, undefined].includes(value)) {
        return null
      }
      if (typeof value === 'boolean') {
        return value
      } else if (typeof value === 'string') {
        const output = value.toLowerCase().trim() === 'true'
        return output
      }
      return null
    },
    getSortComparator: sortComparatorGenericWithoutNulls,
    groupingValueGetter: (value) => {
      if (['', null, undefined].includes(value)) {
        return null
      }
      return value.toString()
    },
  },
  [PropertyTypes.Combobox]: {
    type: 'singleSelect',
    width: ColumnWidths.SMALL,
    groupable: true,
    filterable: false,
  },
  [PropertyTypes.MultiPicklist]: {
    type: 'multipicklist',
    width: ColumnWidths.STANDARD,
    groupable: false,
    filterable: false,
    sortable: false,
  },
  [PropertyTypes.Picklist]: {
    type: 'singleSelect',
    width: ColumnWidths.SMALL,
    groupable: true,
    filterable: true,
    valueGetter: (value) => {
      if (!value) {
        return null
      }
      return value
    },
    getSortComparator: sortComparatorGenericWithoutNulls,
    getGroupComparator: sortComparatorGenericWithoutNulls,
  },
  [PropertyTypes.Currency]: {
    type: 'number',
    width: ColumnWidths.STANDARD,
    groupable: true,
    filterable: true,
  },
  [PropertyTypes.Percent]: {
    type: 'number',
    width: ColumnWidths.STANDARD,
    groupable: true,
    filterable: true,
  },
  [PropertyTypes.Phone]: {
    type: 'string',
    width: ColumnWidths.STANDARD,
    groupable: false,
    filterable: true,
  },
  [PropertyTypes.Email]: {
    type: 'string',
    width: ColumnWidths.STANDARD,
    groupable: true,
    filterable: true,
  },
  [PropertyTypes.Url]: {
    type: 'string',
    width: ColumnWidths.STANDARD,
    groupable: true,
    filterable: true,
  },
  [PropertyTypes.DateTime]: DatetimeColumnDefinition,
  [PropertyTypes.ExistsMarker]: DatetimeColumnDefinition,
}
